import type { AgentRoute, ApiKeyRoute } from "../../api/threads/liveMessages";
import type { RouteOption } from "../../api/threads/routeOptions";

export type BatchRoute = AgentRoute | ApiKeyRoute;

export interface BatchRouteChoice {
  readonly key: string;
  readonly route: BatchRoute;
  /** The route options' answer: the default memory tools come off this route. */
  readonly dropsDefaultReplyTools: boolean;
}

/** One key per route and model, for a `<select>`. */
export function routeKey(route: BatchRoute): string {
  return route.kind === "agent" ? `agent:${route.agent_id}:${route.model_id ?? ""}` : `api:${route.route_id}:${route.model_id}`;
}

/**
 * The routes a batch may run on: those the connector offers that can run every turn of
 * the Workflow. API-key routes come first: a batch makes many calls, and an API key has
 * its own metered quota, while a subscription agent shares the user's personal limits
 * (audit §9.1). Agents stay allowed.
 */
export function batchRouteChoices(options: readonly RouteOption[]): BatchRouteChoice[] {
  const usable = options.filter((option) => option.available && option.runsEveryTurn).map((option) => ({ key: routeKey(option.route), route: option.route, dropsDefaultReplyTools: option.dropsDefaultReplyTools }));
  return [...usable.filter((choice) => choice.route.kind === "api_key"), ...usable.filter((choice) => choice.route.kind === "agent")];
}

/** The suggested route: the first API-key route, else the vetted Thread's own, else the first one. */
export function suggestedBatchRoute(choices: readonly BatchRouteChoice[], threadRoute: BatchRoute | null): string | null {
  const apiKey = choices.find((choice) => choice.route.kind === "api_key");
  if (apiKey !== undefined) return apiKey.key;
  const own = threadRoute === null ? undefined : choices.find((choice) => choice.key === routeKey(threadRoute));
  return own?.key ?? choices[0]?.key ?? null;
}
